/**
 * One post on the client's page: the PostHeader (title, Concept and Creative
 * brief, Version ▾ with the sent versions and their dates once there is more
 * than one), the StatusBanner in the client's words with Approve and Request
 * changes, then the ConceptDocument or the brief. Once the open round is
 * approved the banner goes and the header carries an Approved tag. Every part
 * that takes a comment says so on hover.
 */
import { Button as AriaButton } from "react-aria-components";
import { Check, MessageSquare } from "lucide-react";
import { KitTooltip, KitTooltipTrigger } from "@/app/routes/dashboard/components/kit/Tooltip";
import type { ProductionStaticBody, ProductionVideoBody } from "@/types/productionBrief.type";
import { Button } from "@/components/form/Button";
import PostHeader, { type BriefVersionOption } from "@/app/routes/dashboard/content-calendar/components/PostHeader";
import StatusBanner, { type PostViewport } from "@/app/routes/dashboard/content-calendar/components/StatusBanner";
import { BriefStaticBody, BriefVideoBody } from "@/app/routes/dashboard/content-calendar/components/brief/BriefBodies";
import ConceptDocument, { type ConceptDocumentData } from "@/app/routes/dashboard/content-calendar/concepts/ConceptDocument";
import { IdeaSlideContext, lengthLabel } from "@/app/routes/dashboard/content-calendar/concepts/IdeaSlide";
import { shortDate } from "@/app/routes/dashboard/content-calendar/postState";
import {
  activeConceptVersion,
  briefApproved,
  briefVersionEntries,
  briefVisible,
  clientApproval,
  conceptApproved,
  conceptVersionsAsc,
  REQUEST_CHANGES_LABEL,
  rowMeta,
  sentConceptVersions,
  showsBanner,
  type ClientApproval,
  type ClientBanner,
  type ClientFuture,
  type ClientPost,
  type ClientRound,
} from "../clientReviewModel";
import CommentableParts from "./CommentableParts";
import type { ClientCommentPart, ClientMarker } from "./useClientComments";
import "./clientReview.css";

function conceptData(post: ClientPost, version: number | null): ConceptDocumentData | null {
  const { slot } = post;
  // A static reads as a still at its own ratio: no play, no sound, no running time.
  const still = slot.mediaType === "STATIC";
  const versions = conceptVersionsAsc(slot);
  const chosen = version !== null ? versions.find((entry) => entry.version === version) : activeConceptVersion(slot);
  if (chosen?.snapshot) {
    const snapshot = chosen.snapshot;
    return {
      title: snapshot.title || slot.title,
      synopsis: snapshot.synopsis || null,
      // Every hook the send kept: the slide lists them (Figma 08); the phone's
      // stacked page still shows the first.
      hooks: snapshot.hooks ?? [],
      objectiveClaim: snapshot.objectiveClaim || null,
      pillarName: slot.pillarName,
      durationSeconds: still ? null : snapshot.durationSeconds,
      channels: snapshot.channels,
      reference: snapshot.reference,
      aspectRatio: snapshot.aspectRatio ?? slot.aspectRatio,
      still,
    };
  }
  if (!slot.idea) return null;
  return {
    title: slot.idea.title || slot.title,
    synopsis: slot.idea.synopsis || null,
    hooks: slot.idea.hook ? [slot.idea.hook] : [],
    objectiveClaim: slot.idea.objectiveClaim || null,
    pillarName: slot.pillarName,
    durationSeconds: still ? null : slot.idea.durationSeconds,
    channels: slot.idea.channels,
    reference: slot.idea.reference,
    aspectRatio: slot.idea.aspectRatio ?? slot.aspectRatio,
    still,
  };
}

/** "Creative brief · 5 shots, 60 s · one caption per channel · approved with the idea". */
function briefLine(brief: NonNullable<ClientPost["brief"]>, approved: boolean, askedSeconds: number | null | undefined): string {
  const entries = briefVersionEntries(brief);
  const body = (entries.at(-1)?.body ?? null) as { storyboard?: unknown[]; panels?: unknown[] } | null;
  const shots = Array.isArray(body?.storyboard) ? body!.storyboard!.length : 0;
  const pictures = Array.isArray(body?.panels) ? body!.panels!.length : 0;
  const size =
    brief.kind === "STATIC"
      ? pictures > 1
        ? `a carousel of ${pictures} pictures`
        : "one picture"
      : shots
        ? `${shots} ${shots === 1 ? "shot" : "shots"}${askedSeconds && lengthLabel(askedSeconds) ? `, ${lengthLabel(askedSeconds)}` : ""}`
        : null;
  return ["Creative brief", size, "one caption per channel", approved ? "approved with the idea" : "sent with the idea"].filter(Boolean).join(" · ");
}

/**
 * The header's Approved tag (Pen E4579): Button / Secondary's look with its
 * check, and Tooltip / Label on hover or focus with who, for which client,
 * when and which version.
 */
function ApprovedTag({ approval }: { approval: ClientApproval }) {
  return (
    <KitTooltipTrigger delay={300} closeDelay={100}>
      <AriaButton className="co-cr-approved co-glass-control" aria-label={approval.tooltip}>
        {approval.label}
        <Check strokeWidth={2} aria-hidden="true" />
      </AriaButton>
      <KitTooltip placement="bottom end" offset={6}>
        {approval.tooltip}
      </KitTooltip>
    </KitTooltipTrigger>
  );
}
export default function ClientPostView({
  post,
  tab,
  onTab,
  viewport,
  brandName,
  banner,
  busy,
  viewingVersion,
  onPickVersion,
  onApprove,
  onAskForChanges,
  onCommentPart,
  onHoverPart,
  markers = [],
  activePart = null,
  activeNumber = null,
  onMarker,
  reveal = 0,
  pillarHue,
  future,
  comments,
  onBack,
  monthName,
}: {
  post: ClientPost;
  tab: ClientRound;
  onTab: (tab: ClientRound) => void;
  viewport: PostViewport;
  brandName: string;
  banner: ClientBanner;
  busy: boolean;
  /** The version on screen for the open tab, or null for the current one. */
  viewingVersion: number | null;
  onPickVersion: (version: number | null) => void;
  onApprove: () => void;
  onAskForChanges: () => void;
  /** Open the composer on a part of the page ("the hook"); null when commenting is off. */
  onCommentPart?: ((part: ClientCommentPart) => void) | null;
  /** The hovered or focused part, so the page's C key comments on it. */
  onHoverPart?: (part: ClientCommentPart | null) => void;
  /** The open threads pinned to parts on this tab and version. */
  markers?: ClientMarker[];
  /** The part in hand: the selected thread's or the one being commented on. */
  activePart?: { part: string; key?: string | null } | null;
  /** The number a new comment on the active part takes. */
  activeNumber?: number | null;
  onMarker?: (threadId: string) => void;
  /** Bumped by Show on the page. */
  reveal?: number;
  pillarHue: string | null;
  future?: ClientFuture;
  /** Tablet: a Comments button in the header opens the sheet. */
  comments?: { count: number; onOpen: () => void };
  /** Phone: back to the list. */
  onBack?: () => void;
  monthName: string;
}) {
  const { slot, brief } = post;
  const phone = viewport === "phone";
  const showBrief = tab === "brief" && briefVisible(post) && !!brief;

  const currentConcept = activeConceptVersion(slot)?.version ?? null;
  const briefEntries = brief ? briefVersionEntries(brief) : [];
  const versions: BriefVersionOption[] = showBrief
    ? [...briefEntries].reverse().map((entry) => ({ version: entry.version }))
    : sentConceptVersions(slot);
  const currentVersion = showBrief ? (brief?.version ?? null) : currentConcept;
  const data = showBrief ? null : conceptData(post, viewingVersion);
  const briefEntry = showBrief ? (briefEntries.find((entry) => entry.version === (viewingVersion ?? brief!.version)) ?? briefEntries.at(-1)) : null;
  const askedSeconds = activeConceptVersion(slot)?.snapshot.durationSeconds ?? slot.idea?.durationSeconds ?? null;

  const secondary = banner.canAskForChanges ? (
    <Button
      size="small"
      variant="secondary"
      buttonStyle="outline"
      isDisabled={busy}
      onPress={onAskForChanges}
      icon={MessageSquare}
      iconPosition="right"
      iconClassName="size-3.5"
      className="shrink-0"
    >
      {REQUEST_CHANGES_LABEL}
    </Button>
  ) : undefined;
  const round: ClientRound = showBrief ? "brief" : "concept";
  const approval = clientApproval(post, round, brandName);
  const bannerShown = showsBanner(post, round, viewingVersion);

  return (
    <article className="flex min-w-0 flex-col" aria-label={slot.idea?.title || slot.title}>
      <PostHeader
        title={slot.idea?.title || slot.title || "Post"}
        viewport={viewport}
        phase={showBrief ? "brief" : "concept"}
        onPhaseChange={(phase) => onTab(phase === "brief" ? "brief" : "concept")}
        conceptApproved={conceptApproved(slot)}
        briefApproved={briefApproved(brief)}
        briefExists={briefVisible(post)}
        briefTab="hidden"
        showDistribution={false}
        versions={versions}
        versionsLabel={showBrief ? "Creative brief versions" : "Idea versions"}
        currentVersion={currentVersion}
        viewingVersion={viewingVersion}
        onPickVersion={onPickVersion}
        comments={comments}
        tag={approval ? <ApprovedTag approval={approval} /> : undefined}
        phoneTop={
          phone && onBack
            ? {
                monthLabel: monthName,
                dateLabel: shortDate(slot.scheduledAt) ?? "",
                meta: rowMeta(post, future),
                onBack,
              }
            : undefined
        }
      />

      {bannerShown ? (
        <div className="mt-5 flex flex-col gap-4">
          <StatusBanner
            banner={phone ? { ...banner, primary: undefined } : banner}
            viewport={viewport}
            busy={busy}
            onPrimary={onApprove}
            secondary={secondary}
          />
        </div>
      ) : null}

      <CommentableParts
        enabled={!!onCommentPart}
        touch={phone}
        onComment={(part) => onCommentPart?.(part)}
        onHoverPart={onHoverPart}
        markers={markers}
        active={activePart}
        activeNumber={activeNumber}
        onMarker={onMarker}
        reveal={reveal}
      >
      <div className={phone ? "mt-6" : "mt-8"}>
        {showBrief && briefEntry && brief ? (
          brief.kind === "STATIC" ? (
            <BriefStaticBody body={briefEntry.body as ProductionStaticBody} audience="client" />
          ) : (
            <BriefVideoBody
              body={briefEntry.body as ProductionVideoBody}
              askedSeconds={askedSeconds}
              aspectRatio={slot.aspectRatio}
              audience="client"
            />
          )
        ) : data ? (
          // Figma 08: the client reads the idea as the same slide the team does.
          <IdeaSlideContext.Provider
            value={{
              brandName,
              scheduledAt: slot.scheduledAt,
              pillarName: slot.pillarName || null,
              pillarHue,
            }}
          >
            <ConceptDocument data={data} audience="client" brandName={brandName} pillarHue={pillarHue} />
          </IdeaSlideContext.Provider>
        ) : (
          <div className="grid min-h-[320px] place-items-center rounded-card border border-co-line bg-co-surface px-6 text-center">
            <div className="flex max-w-[40ch] flex-col items-center gap-2">
              <p className="font-section text-[22px] font-bold text-co-text">A new version is on its way</p>
              <p className="text-[14px] leading-relaxed text-co-muted">
                The next version of this idea shows here as soon as it is sent to you.
              </p>
            </div>
          </div>
        )}
      </div>
      </CommentableParts>

      {!showBrief && brief && briefVisible(post) ? (
        // Figma 08: the brief went with the idea; one line under the slide opens it.
        <div className="mt-8 flex flex-wrap items-center gap-3 border-y border-co-line py-4">
          <p className="min-w-0 flex-1 text-[14px] text-co-text2">{briefLine(brief, briefApproved(brief), askedSeconds)}</p>
          <button
            type="button"
            onClick={() => onTab("brief")}
            className="text-[14px] font-medium text-co-text outline-none hover:underline focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            Show the brief
          </button>
        </div>
      ) : null}
    </article>
  );
}
